import { useEffect, useRef, useState } from "react";
import { useSocketContext } from "../context/SocketContext";
import { buildPresenceFromUsersSnapshot } from "../utils/teamPresenceUsersSnapshot";
import {
  mergePresenceSocketRowsForMainApp,
  clearMainAppPresenceSocketMergeCache,
} from "../utils/teamPresenceUsersSocket";

/** Agrupa rajadas de userOnline/userOffline antes de um único setState (grandes equipes / muitas abas). */
const DELTA_FLUSH_MS = 80;

/**
 * Presença por socket (evento `users` como na tela administrar/usuarios do Xpro antigo).
 * Conta abas = ocorrências do mesmo id em payload.data.
 * Linhas com `client: 'xpro4'` incluem rota, tempos e foco por aba; demais contam como legado.
 * Payload `users` pode vir como objeto único ou `{ data: [...] }`; linhas mínimas são enriquecidas com cache por `socketId`/tab.
 * O emit periódico de `users` fica em `TeamPresenceSocketEmitter` (toda aba logada).
 */
export function useTeamSocketPresence(userData) {
  const { socket } = useSocketContext();
  const [tabsByUserId, setTabsByUserId] = useState(() => new Map());
  const [presenceDetailByUserId, setPresenceDetailByUserId] = useState(() => new Map());
  const [namesByUserId, setNamesByUserId] = useState(() => new Map());
  const [socketConnected, setSocketConnected] = useState(false);
  const deltaRef = useRef(new Map());
  const flushTimerRef = useRef(null);

  useEffect(() => {
    if (!socket) {
      setSocketConnected(false);
      setNamesByUserId(new Map());
      clearMainAppPresenceSocketMergeCache();
      return undefined;
    }

    const flushDeltas = () => {
      flushTimerRef.current = null;
      const deltas = deltaRef.current;
      if (!deltas.size) return;
      deltaRef.current = new Map();
      setTabsByUserId((prev) => {
        const next = new Map(prev);
        for (const [uid, d] of deltas) {
          const cur = next.get(uid) || 0;
          const n = cur + d;
          if (n <= 0) next.delete(uid);
          else next.set(uid, n);
        }
        return next;
      });
    };

    const queueDelta = (uid, delta) => {
      deltaRef.current.set(uid, (deltaRef.current.get(uid) || 0) + delta);
      if (flushTimerRef.current == null) {
        flushTimerRef.current = setTimeout(flushDeltas, DELTA_FLUSH_MS);
      }
    };

    const cancelDeltaFlush = () => {
      if (flushTimerRef.current != null) {
        clearTimeout(flushTimerRef.current);
        flushTimerRef.current = null;
      }
      deltaRef.current = new Map();
    };

    const applyUsersPayload = (payload) => {
      if (payload == null) return;
      const enriched = mergePresenceSocketRowsForMainApp(payload);
      cancelDeltaFlush();
      const { counts, detail, names } = buildPresenceFromUsersSnapshot(enriched);
      setTabsByUserId(counts);
      setPresenceDetailByUserId(detail);
      setNamesByUserId(names);
    };

    const onConnect = () => setSocketConnected(true);
    const onDisconnect = () => {
      setSocketConnected(false);
      clearMainAppPresenceSocketMergeCache();
    };

    const onUserOnline = (payload) => {
      const uid = Number(payload?.id);
      if (!Number.isFinite(uid)) return;
      const nm = payload?.name != null ? String(payload.name).trim() : "";
      if (nm) {
        setNamesByUserId((prev) => {
          if (prev.has(uid)) return prev;
          const next = new Map(prev);
          next.set(uid, nm);
          return next;
        });
      }
      queueDelta(uid, 1);
    };

    const onUserOffline = (payload) => {
      const uid = Number(payload?.id);
      if (!Number.isFinite(uid)) return;
      queueDelta(uid, -1);
    };

    const onUsers = (payload) => {
      applyUsersPayload(payload);
    };

    socket.on("connect", onConnect);
    socket.on("disconnect", onDisconnect);
    socket.on("users", onUsers);
    socket.on("userOnline", onUserOnline);
    socket.on("userOffline", onUserOffline);
    setSocketConnected(socket.connected);

    return () => {
      cancelDeltaFlush();
      socket.off("connect", onConnect);
      socket.off("disconnect", onDisconnect);
      socket.off("users", onUsers);
      socket.off("userOnline", onUserOnline);
      socket.off("userOffline", onUserOffline);
    };
  }, [socket, userData?.id, userData?.nome, userData?.name]);

  return { tabsByUserId, presenceDetailByUserId, namesByUserId, socketConnected };
}
